import useNavigationStore from '@src/stores/navigationStore';
import useRegistrationStore from '@src/stores/registrationStore';

import styles from './AccountOptions.module.scss';

import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import BackButton from '@src/components/_shared/BackButton';
import Container from '@src/components/_shared/Container';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import ValidateChoiceButton from '@src/components/_shared/UI/ValidateChoiceButton';
import { useMutation, useQuery } from '@tanstack/react-query';
import { companyUpdate, personUpdate } from '@src/api/Registration';
import { useState } from 'react';
import { getReferencialData } from '@src/api/Referencial';
import Loader from '@src/components/_shared/UI/Loader';
import { handleApiError } from '@src/utilities/functions';

export default function AccountOptions() {
    const { setSection, setBlock } = useNavigationStore();
    const { setField, registration } = useRegistrationStore();
    const [srdLevel, setSrdLevel] = useState<boolean>(false);
    const [srdWarning, setSrdWarning] = useState<boolean>(false);
    const isMeilleurTaux = registration.partner_informations.brand.sitecode === 'DAS';

    const _onGoBack = () => {
        setSection(5)
        setBlock('AccountInformations')
    };

    const { data } = useQuery({
        queryKey: [`getSrdLevelsData`],
        queryFn: () => getReferencialData({type: 'srd-ror-levels'})
    })

    const { mutate: updatePerson, isPending: updatePersonIsPending } = useMutation({
        mutationFn: (srd_ror: any) => personUpdate({key: 'srd_ror', value: srd_ror, is_psi: registration.partner_informations.is_partner, person_id: registration.person_id}), 
        onSuccess: () => setBlock('PriceList'),
        onError: (error: any) => handleApiError(error)
    });

    const { mutate, isPending } = useMutation({
        mutationFn: () => {
            const { basic, derivative_products, srd_ror } = registration.company;

            return companyUpdate({
                value: { basic, derivative_products: derivative_products.access, srd_ror: isMeilleurTaux ? false : srd_ror.access },
                is_psi: registration.partner_informations.is_partner,
                person_id: registration.person_id
            });
        },
        onSuccess: () => {
            if (registration.company.srd_ror.access || registration.company.derivative_products.access) {
                setSrdWarning(true);
            } else {
                setBlock('PriceList')
            }
        },
        onError: (error: any) => handleApiError(error)
    });

    const handleSelectDerivativeProductsAccess = (value: boolean) => {
        setField('company.derivative_products.access', value);
    }

    const handleSelectSrdAccess = (value: boolean) => {
        setField('company.srd_ror.access', value);
        setField('person.srd_ror.access', value);
        if (value === false) {
            const { level, ...updatedSrdRor } = registration.person.srd_ror;
            setField('person.srd_ror', {
                ...updatedSrdRor,
                validation: false
            });
        }
    }

    const _onAcceptSRDWarning = () => {
        if (registration.company.srd_ror.access) {
            setSrdLevel(true);
            setSrdWarning(false);
        } else {
            setBlock('PriceList')
            setSrdWarning(false);
        }
    }

    const _handleSelectSrdLevel = (key: string) => {
        setField('person.srd_ror.level', key);
        setField('person.srd_ror.access', true);
        setField('person.srd_ror.validation', true);
        updatePerson(registration.person.srd_ror);
    }

    if (updatePersonIsPending) {
        return (
            <Container>
                <Loader type="SPINNER" />
            </Container>
        )
    }

    if (srdLevel) {
        return (
            <Container>
                <BackButton onClick={() => setSrdLevel(false)}/>
                <BlockTitle title="Quel est votre niveau de connaissance du SRD/ROR ?" />
                <Subtitle content="Veuillez sélectionner le niveau le plus approprié." />
                {updatePersonIsPending ?
                    <Loader type="SPINNER" />
                    :
                    <div className={styles.buttonsList}>
                        {Object.entries(data).map(([key, value]) => (
                            <ValidateChoiceButton
                                key={key}
                                title={value as string}
                                fullWidth
                                selected={false}
                                onValidate={() => _handleSelectSrdLevel(key)}
                            />
                        ))}
                    </div>
                }
            </Container>
        )
    }

    if (srdWarning) {
        return (
            <Container>
                <BackButton onClick={() => setSrdWarning(false)}/>
                <BlockTitle title="Avertissement pour l’accès au SRD/ROR ou au Marché des produits dérivés" />
                <Subtitle content={`En cliquant sur “J’accepte”, j’accepte les conditions et je reconnais posséder une parfaite connaissance de l'ensemble des mécanismes boursiers du Service de Règlement Différé (SRD/ROR), de la vente à découvert et / ou des Marchés des produits dérivés, de ses conditions de fonctionnement, des risques inhérents à ces opérations (notamment le risque de perdre plus que mon capital) dont j'ai pleinement conscience, des engagements qui m'incomberont du fait de ma participation à ces opérations et des règles relatives aux couvertures de positions. * Sous réserve de l’acceptation de ${registration.partner_informations.is_partner ? 'Direct Securities' : 'Bourse Direct'}`} />
                <ValidateButton title="J'accepte" onClick={_onAcceptSRDWarning} />
            </Container>
        );
    }

    return (
        <Container>
            <BackButton onClick={_onGoBack}/>
            <BlockTitle title="Options de compte" />
            <Subtitle content="Souhaitez-vous avoir accès aux Marchés des produits dérivés ?" />
            <div className={styles.buttons}>
                <ValidateChoiceButton title="Oui" selected={registration.company.derivative_products.access === true} onValidate={() => handleSelectDerivativeProductsAccess(true)} />
                <ValidateChoiceButton title="Non" selected={registration.company.derivative_products.access === false} onValidate={() => handleSelectDerivativeProductsAccess(false)} />
            </div>
            {registration.company.derivative_products.access !== null && (registration.company.srd_ror.access !== null || !isMeilleurTaux) ?
                <>
                    <Subtitle content="Souhaitez-vous pouvoir accéder au SRD ?" />
                    <div className={styles.buttons}>
                        <ValidateChoiceButton title="Oui" selected={registration.company.srd_ror.access === true} onValidate={() => handleSelectSrdAccess(true)} />
                        <ValidateChoiceButton title="Non" selected={registration.company.srd_ror.access === false} onValidate={() => handleSelectSrdAccess(false)} />
                    </div>
                </>
                : null}

            {registration.company.derivative_products.access !== null && (registration.company.srd_ror.access !== null || isMeilleurTaux) ?
                <ValidateButton title="Valider" onClick={mutate} disabled={isPending} onLoading={isPending} />
                : null}
        </Container>
    );
}
